<template>
    <v-container fluid class="pa-3" v-if="sessionActive">

        <material-header title="External suppliers"
                         help="external-suppliers-list"
                         info="Manage external suppliers"
                         icon="mdi-network-outline">

            <template slot="buttons">

                <!-- Reload -->
                <v-tooltip top color="primary">
                    <template v-slot:activator="{ on }">
                        <v-btn :disabled="!hasPermission(minPermission)"
                               @click="reloadClicked()"
                               v-on="on"
                               icon>
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </template>
                    <span>Reload data</span>
                </v-tooltip>

                <v-btn fab color="primary" elevation="0" small dark @click="addSupplierClicked()" class="ml-3">
                    <v-icon>mdi-plus</v-icon>
                </v-btn>

            </template>

        </material-header>

        <v-alert v-if="!hasPermission(minPermission)"
                 class="body-2"
                 type="error"
        >
            Your user account doesn't have sufficient permissions to use this feature
        </v-alert>

        <v-row v-if="hasPermission(minPermission)">

            <v-col cols="12" class="pt-0">

                <v-card flat>
                    <v-card-text>

                        <v-data-table
                            :headers="headers"
                            :items="suppliers"
                            :search="search"
                            :loading="loading"
                            :sort-by="sortBy"
                            :sort-desc="sortDesc"
                            :footer-props="{itemsPerPageOptions: [10,25,50,100, -1]}"
                        >
                            <template v-slot:body="{ items }">
                                <tbody>

                                <!-- NO DATA INFO -->
                                <tr v-if="items.length===0 && !loading">
                                    <td colspan="6">
                                        <v-alert
                                            color="primary"
                                            class="mt-4 body-2"
                                            icon="mdi-information">
                                            No external suppliers found. Click on the PLUS -icon to create one.
                                        </v-alert>
                                    </td>
                                </tr>

                                <!-- ITEMS -->
                                <tr v-for="item in items" :key="item.id">

                                    <!-- ID -->
                                    <td>
                                        <v-chip color="primary">{{ item.id }}</v-chip>
                                    </td>

                                    <!-- NAME -->
                                    <td @click.stop="editClicked(item)" class="cursor-pointer">
                                        <v-tooltip open-delay="250" max-width="220" top color="primary">
                                            <template v-slot:activator="{ on }">
                                                <span v-on="on">{{ item.name }}</span>
                                            </template>
                                            <span>
                                                Click to edit
                                            </span>
                                        </v-tooltip>
                                    </td>

                                    <td>
                                        <div v-if="item.primary_contact_name !== null" class="text-no-wrap">
                                            {{ item.primary_contact_name }}
                                        </div>
                                    </td>

                                    <td>
                                        <div v-if="item.primary_contact_phone !== null" class="d-flex text-no-wrap">
                                            <v-icon small class="mr-2">mdi-phone</v-icon>
                                            <div>
                                                {{ item.primary_contact_phone }}
                                            </div>
                                        </div>
                                    </td>

                                    <td>
                                        <div v-if="item.primary_contact_email !== null" class="d-flex text-no-wrap">
                                            <v-icon small class="mr-2">mdi-at</v-icon>
                                            <div>
                                                {{ item.primary_contact_email }}
                                            </div>
                                        </div>
                                    </td>

                                    <!-- ID -->
                                    <td>
                                        <avatar v-if="item.created_by"
                                                :image-url="item.created_by.photo_url"
                                                :person-name="item.created_by.name"></avatar>
                                    </td>

                                    <td>
                                        {{ toHumanFrom(item.created_at) }}
                                    </td>

                                    <!-- ACTIONS -->
                                    <td class="text-right">

                                        <div style="white-space: nowrap;">

                                            <v-tooltip color="primary" top>
                                                <template v-slot:activator="{ on }">
                                                    <v-btn :disabled="!hasPermission('delete recruitment sources')"
                                                           icon
                                                           text
                                                           class="ma-0"
                                                           v-on="on"
                                                           @click="deleteClicked(item)"
                                                           color="secondary">
                                                        <v-icon light small color="white">mdi-delete</v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Delete supplier</span>
                                            </v-tooltip>
                                        </div>
                                    </td>
                                </tr>
                                </tbody>
                            </template>

                        </v-data-table>

                        <json v-if="false" :data="suppliers"/>
                    </v-card-text>
                </v-card>

            </v-col>

        </v-row>

        <confirmation-dialog v-model="deleteConfirmationDialogModel"
                             headline="Delete confirmation"
                             :delete="true"
                             :description="deleteDescription"
                             type="delete"
                             @selected="deleteDo"
        ></confirmation-dialog>

        <supplier-dialog v-if="createMode && subPanels"
                         :sub-panels="subPanels"
                         :key="supplierDialogKey"
                         v-model="supplierDialogModel"
        ></supplier-dialog>

        <supplier-dialog v-if="!createMode && subPanels"
                         :sub-panels="subPanels"
                         :key="supplierDialogKey"
                         v-model="supplierDialogModel"
                         :data="supplierUnderEdit"
        ></supplier-dialog>

    </v-container>
</template>

<script>

import Avatar from '../../../components/UI/Avatar';
import {timeDisplay} from '../../../components/mixins/timeDisplayMixin';
import Json from 'vue-json-pretty';
import Store from '../store';
import {mapState, mapMutations, mapActions} from 'vuex';
import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
import MaterialHeader from '../../../components/UI/MaterialHeader';
import SupplierDialog from "./SupplierDialog";
import ConfirmationDialog from "../../../components/UI/ConfirmationDialog";

const vuexModule = 'ExternalSuppliers';

export default {

    mixins: [activeSessionChecker, timeDisplay],

    components: {
        ConfirmationDialog,
        SupplierDialog,
        Json,
        MaterialHeader,
        Avatar
    },

    data() {
        return {
            minPermission: 'view panel settings',
            loading: false,
            supplierDialogModel: false,
            createMode: false,
            search: '',
            sortBy: 'name',
            sortDesc: false,
            headers: [
                {text: 'ID', align: 'left', sortable: true, value: 'id', width: '50px'},
                {text: 'NAME', align: 'left', sortable: true, value: 'name', width: '200px'},
                {text: 'CONTACT NAME', align: 'left', sortable: false},
                {text: 'CONTACT PHONE', align: 'left', sortable: false},
                {text: 'CONTACT EMAIL', align: 'left', sortable: false},
                {text: 'CREATED BY', align: 'left', sortable: true, value: 'created_by', width: '130px'},
                {text: 'CREATED', align: 'left', sortable: true, value: 'created_at', width: '200px'},
                {text: 'ACTIONS', sortable: false, align: 'right', width: '40px'},
            ],
            supplierUnderEdit: null,
            supplierUnderDelete: null,
            supplierDialogKey: 1,
            deleteConfirmationDialogModel: false,
            deleteDescription: "Please make sure you are not using this supplier in your project. " +
                "Any incoming traffic from this supplier will be immediately blocked Are you want to delete this supplier?"
        }
    },
    computed: {
        ...mapState(vuexModule, [
            'suppliers',
        ]),

        ...mapState('SubPanels', [
            'subPanels',
        ]),
    },

    methods: {
        ...mapActions(vuexModule, [
            'getAllSuppliers',
            'updateSupplier',
            'deleteSupplier'
        ]),

        ...mapActions('SubPanels', [
            'loadSubPanels',
        ]),

        ...mapMutations(vuexModule, []),

        addSupplierClicked() {
            this.createMode = true;
            this.supplierDialogKey++;
            this.supplierDialogModel = true;
        },

        deleteClicked(supplier) {
            this.supplierUnderDelete = supplier;
            this.deleteConfirmationDialogModel = true;
        },

        deleteDo(confirmed) {

            if (confirmed) {

                this.deleteSupplier(this.supplierUnderDelete)
                    .then(response => {
                        Bus.$emit('flash-message', {type: 'success', message: 'Supplier deleted!'});
                    })
                    .catch(error => {
                        console.log(error.response);
                        Bus.$emit('flash-message', {type: 'error', message: 'OOPS We failed to delete the supplier... Try again later!'});
                    })
                    .finally(() => {
                        this.loading = false;
                        this.supplierUnderDelete = null;
                    })
            }
        },

        editClicked(supplier) {
            this.createMode = false;
            this.supplierUnderEdit = supplier;
            this.supplierDialogKey++;
            this.supplierDialogModel = true;
        },

        toggleClicked(supplier) {

            this.updateSupplier(supplier)
                .then(response => {

                    const message = supplier.enabled ? 'activated...' : 'deactivated...'

                    Bus.$emit('flash-message', {
                        type: 'success',
                        message: 'Supplier ' + message
                    });
                })
                .catch(response => {
                    console.log(error.response);
                    Bus.$emit('flash-message', {type: 'error', message: 'OOPS We failed to save the supplier... Try again later!'});
                })
        },

        reloadClicked() {
            this.loadSuppliers();
        },

        loadSuppliers() {
            this.loading = true;

            this.getAllSuppliers()
                .then(response => {

                })
                .catch(error => {
                    console.log(error.response);
                    Bus.$emit('flash-message', {type: 'error', message: 'OOPS We failed loading suppliers... Try again later!'});

                })
                .finally(() => {
                    this.loading = false;
                })
        }
    },

    mounted() {
        this.loadSubPanels();
        this.loadSuppliers();
    },

    created() {
        if (!this.$store.state[vuexModule]) {
            this.$store.registerModule(vuexModule, Store);
        }
    }
}

</script>
